<!--
* @Author: AI Assistant
* @Date: 2026-01-12
* @Description: API Keys 页面 - API 密钥管理
-->
<template>
    <div class="keys-page p-4 md:p-8 max-w-[1120px] mx-auto">
        <h1 class="sr-only">{{ t('api.keysTitle') }}</h1>
        <!-- 页面标题 -->
        <div
            class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8"
        >
            <div>
                <h2 class="text-xl md:text-2xl font-bold text-white">
                    {{ t('api.keysTitle') }}
                </h2>
                <p class="text-xs md:text-sm text-zinc-400 mt-1">
                    {{ t('api.keysDesc') }}
                </p>
            </div>
            <button
                class="w-full sm:w-auto flex items-center justify-center gap-2 text-black px-4 py-2 rounded-lg font-bold transition-all shadow-lg text-sm create-key-btn"
                @click="openCreateModal"
            >
                <PlusIcon class="w-[18px] h-[18px]" />
                {{ t('api.createNewKey') }}
            </button>
        </div>

        <!-- 密钥列表表格 -->
        <div
            ref="tableWrapRef"
            v-loading="loading"
            class="keys-table-wrap bg-[#18181b] border border-zinc-800 rounded-xl overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table class="w-full text-left border-collapse min-w-[700px]">
                    <thead
                        class="bg-black/50 border-b border-zinc-800 text-zinc-500 text-[10px] md:text-xs font-semibold"
                    >
                        <tr>
                            <th class="px-6 py-4">
                                {{ t('api.description') }}
                            </th>
                            <th class="px-6 py-4">{{ t('api.secretKey') }}</th>
                            <th class="px-6 py-4">{{ t('api.createTime') }}</th>
                            <th class="px-6 py-4 text-right">
                                {{ t('api.actions') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-zinc-800">
                        <tr
                            v-for="key in apiKeys"
                            :key="key.api_key"
                            class="hover:bg-white/5 transition-colors group"
                        >
                            <td
                                class="px-6 py-4 text-white font-medium text-sm"
                            >
                                {{ key.desc }}
                            </td>
                            <td class="px-6 py-4">
                                <div
                                    class="flex items-center gap-2 font-mono text-xs text-lime-400 bg-lime-400/10 px-3 py-1.5 rounded border border-lime-400/20 w-fit cursor-pointer hover:bg-lime-400/20 transition-colors"
                                    @click="copyToClipboard(key.api_key)"
                                >
                                    <KeyIcon class="w-3 h-3 text-lime-400" />
                                    {{ maskApiKey(key.api_key) }}
                                </div>
                            </td>
                            <td class="px-6 py-4 text-zinc-500 text-xs">
                                {{ formatDate(key.create_time) }}
                            </td>
                            <td class="px-6 py-4 text-right">
                                <button
                                    class="text-zinc-500 hover:text-red-400 transition-colors p-2 hover:bg-red-900/20 rounded"
                                    @click="confirmDelete(key)"
                                >
                                    <TrashIcon class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>

                        <!-- 空状态 -->
                        <tr v-if="!loading && apiKeys.length === 0">
                            <td
                                colspan="4"
                                class="px-6 py-12 text-center text-zinc-500"
                            >
                                <div class="flex flex-col items-center gap-3">
                                    <KeyIcon class="w-12 h-12 opacity-30" />
                                    <p>{{ t('api.noKeys') }}</p>
                                    <button
                                        class="text-lime-400 hover:underline text-sm"
                                        @click="openCreateModal"
                                    >
                                        {{ t('api.createFirstKey') }}
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="total > 0" class="pagination-container">
            <div class="pagination-text">
                {{ t('myCreations.pagination.total', { total }) }}
            </div>
            <el-pagination
                v-model:current-page="currentPage"
                :page-size="pageSize"
                :total="total"
                layout="prev, pager, next"
                :hide-on-single-page="false"
                class="custom-pagination"
                @current-change="handlePageChange"
            />
        </div>

        <!-- 创建密钥弹窗 -->
        <el-dialog
            v-model="showCreateModal"
            :title="t('api.createKeyTitle')"
            width="450px"
            :close-on-click-modal="false"
            align-center
            class="api-key-dialog"
        >
            <div class="space-y-4">
                <div>
                    <label class="block text-sm text-zinc-400 mb-2">
                        {{ t('api.keyNameLabel') }}
                    </label>
                    <el-input
                        v-model="newKeyDesc"
                        :placeholder="t('api.keyNamePlaceholder')"
                        class="api-key-input"
                    />
                </div>
                <div class="flex justify-end gap-3 mt-6">
                    <button
                        class="px-4 py-2 text-zinc-400 hover:text-white text-sm"
                        @click="closeCreateModal"
                    >
                        {{ t('api.cancel') }}
                    </button>
                    <button
                        class="px-4 py-2 text-black font-bold rounded-lg text-sm transition-all create-key-btn"
                        :disabled="createLoading"
                        @click="createApiKey"
                    >
                        {{
                            createLoading ? t('api.creating') : t('api.create')
                        }}
                    </button>
                </div>
            </div>
        </el-dialog>

        <!-- 删除确认弹窗 -->
        <el-dialog
            v-model="showDeleteModal"
            :title="t('api.deleteConfirm')"
            width="400px"
            align-center
            class="api-key-dialog"
        >
            <div class="text-zinc-300">
                {{ t('api.deleteMessage') }}
                <span class="text-lime-400 font-mono">{{
                    keyToDelete?.desc
                }}</span>
                {{ t('api.deleteWarning') }}
            </div>
            <template #footer>
                <div class="flex justify-end gap-3">
                    <button
                        class="px-4 py-2 text-zinc-400 hover:text-white text-sm"
                        @click="showDeleteModal = false"
                    >
                        {{ t('api.cancel') }}
                    </button>
                    <button
                        class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg text-sm"
                        :disabled="deleteLoading"
                        @click="deleteApiKey"
                    >
                        {{ t('api.delete') }}
                    </button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script setup lang="ts">
import { ref, h, onMounted, computed } from 'vue'
import { definePageMeta, useHead } from '#imports'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { ElMessage } from 'element-plus'
import {
    createApiKeyApi,
    getApiKeysApi,
    deleteApiKeyApi,
    type ApiKeyItem,
} from '~/api/api'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()

// 使用多语言 SEO
const { canonicalUrl } = useMultilingualSeo({
    pageType: 'apiKeys',
    robots: 'noindex, follow',
})

useHead({
    script: [
        // BreadcrumbList Schema
        {
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'WeryAI',
                            item: 'https://www.weryai.com',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('api.keysTitle'),
                            item: canonicalUrl.value,
                        },
                    ],
                })
            ),
        },
    ],
})

// ========== 图标组件 ==========
const PlusIcon = {
    render: () =>
        h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                width: '18',
                height: '18',
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                'stroke-width': '2',
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
            },
            [h('path', { d: 'M5 12h14' }), h('path', { d: 'M12 5v14' })]
        ),
}

const KeyIcon = {
    render: () =>
        h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                width: '12',
                height: '12',
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                'stroke-width': '2',
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
            },
            [
                h('path', {
                    d: 'm15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4',
                }),
                h('path', { d: 'm21 2-9.6 9.6' }),
                h('circle', { cx: '7.5', cy: '15.5', r: '5.5' }),
            ]
        ),
}

const TrashIcon = {
    render: () =>
        h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                width: '16',
                height: '16',
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                'stroke-width': '2',
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
            },
            [
                h('path', { d: 'M10 11v6' }),
                h('path', { d: 'M14 11v6' }),
                h('path', { d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' }),
                h('path', { d: 'M3 6h18' }),
                h('path', { d: 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }),
            ]
        ),
}

// ========== 数据状态 ==========
const apiKeys = ref<ApiKeyItem[]>([])
const loading = ref(false)
const createLoading = ref(false)
const deleteLoading = ref(false)

// ========== 分页 ==========
const currentPage = ref(1)
const pageSize = 20
const total = ref(0)

// ========== 弹窗状态 ==========
const showCreateModal = ref(false)
const showDeleteModal = ref(false)
const newKeyDesc = ref('')
const keyToDelete = ref<ApiKeyItem | null>(null)

// ========== 辅助函数 ==========
const maskApiKey = (key: string): string => {
    return `${key.substring(0, 8)}...${key.substring(key.length - 4)}`
}

const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })
}

const copyToClipboard = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text)
        ElMessage.success(t('api.copied'))
    } catch {
        ElMessage.error(t('api.copyFailed'))
    }
}

// ========== 获取密钥列表 ==========
const fetchApiKeys = async () => {
    loading.value = true
    try {
        const res = await getApiKeysApi({
            page: currentPage.value,
            page_size: pageSize,
        })
        if (res.success && res.data) {
            apiKeys.value = res.data.content || []
            total.value = res.data.total || 0
        }
    } catch (error) {
        console.error('获取 API Keys 失败:', error)
        ElMessage.error('获取密钥列表失败')
    } finally {
        loading.value = false
    }
}

// ========== 创建密钥 ==========
const openCreateModal = () => {
    newKeyDesc.value = ''
    showCreateModal.value = true
}

const closeCreateModal = () => {
    showCreateModal.value = false
    newKeyDesc.value = ''
}

const createApiKey = async () => {
    if (!newKeyDesc.value.trim()) {
        ElMessage.warning(t('api.pleaseEnterDesc'))
        return
    }

    createLoading.value = true
    try {
        const res = await createApiKeyApi({
            desc: newKeyDesc.value,
            status: 'ACTIVE',
        })
        if (res.success) {
            closeCreateModal()
            currentPage.value = 1
            await fetchApiKeys()
            ElMessage.success(t('api.createSuccess'))
        } else {
            ElMessage.error(res.message || t('api.createFailed'))
        }
    } catch (error) {
        console.error('创建 API Key 失败:', error)
        ElMessage.error(t('api.createFailed'))
    } finally {
        createLoading.value = false
    }
}

// ========== 删除密钥 ==========
const confirmDelete = (key: ApiKeyItem) => {
    keyToDelete.value = key
    showDeleteModal.value = true
}

const deleteApiKey = async () => {
    if (!keyToDelete.value) return

    deleteLoading.value = true
    try {
        const res = await deleteApiKeyApi(keyToDelete.value.api_key)
        if (res.success) {
            if (apiKeys.value.length === 1 && currentPage.value > 1) {
                currentPage.value--
            }
            await fetchApiKeys()
            ElMessage.success(t('api.deleteSuccess'))
            showDeleteModal.value = false
            keyToDelete.value = null
        } else {
            ElMessage.error(res.message || t('api.deleteFailed'))
        }
    } catch (error) {
        console.error('删除 API Key 失败:', error)
        ElMessage.error(t('api.deleteFailed'))
    } finally {
        deleteLoading.value = false
    }
}

const tableWrapRef = ref<HTMLElement | null>(null)

const handlePageChange = () => {
    tableWrapRef.value?.scrollIntoView({ block: 'start' })
    fetchApiKeys()
}

// ========== 生命周期 ==========
onMounted(() => {
    fetchApiKeys()
})
</script>

<style scoped lang="less">
.keys-page {
    animation: fadeIn 0.3s ease-out;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.create-key-btn {
    background-color: rgba(153, 247, 104, 1);
    box-shadow: 0 10px 15px -3px rgba(153, 247, 104, 0.2);

    &:hover {
        opacity: 0.9;
    }
}

.keys-table-wrap {
    position: relative;
    z-index: 0;
    scroll-margin-top: calc(
        var(--app-header-bottom, 75px) + var(--api-tab-height, 57px) + 12px
    );

    :deep(.el-loading-mask) {
        z-index: 1;
        border-radius: inherit;
    }
}

.pagination-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 20px;
}

.pagination-text {
    color: #71717a;
    font-size: 12px;
    text-align: center;
}

.custom-pagination {
    --el-pagination-bg-color: transparent;
    --el-pagination-button-color: #a1a1aa;
    --el-pagination-hover-color: #99f768;
    --el-pagination-button-disabled-bg-color: transparent;
    --el-pagination-button-disabled-color: #3f3f46;

    :deep(.el-pager li) {
        background: transparent;
        color: #a1a1aa;
    }

    :deep(.el-pager li.is-active) {
        color: #99f768;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>

<style lang="less">
// Element Plus Dialog 深色主题样式覆盖
.api-key-dialog {
    .el-dialog {
        background: #18181b;
        border: 1px solid #27272a;
        border-radius: 16px;
    }

    .el-dialog__header {
        padding: 20px 24px 16px;
        border-bottom: 1px solid #27272a;

        .el-dialog__title {
            color: #ffffff;
            font-weight: 600;
        }

        .el-dialog__headerbtn {
            .el-dialog__close {
                color: #71717a;

                &:hover {
                    color: #ffffff;
                }
            }
        }
    }

    .el-dialog__body {
        padding: 24px;
        color: #a1a1aa;
    }

    .el-dialog__footer {
        padding: 16px 24px 20px;
        border-top: 1px solid #27272a;
    }

    // Input 样式
    .api-key-input {
        .el-input__wrapper {
            background: #000000;
            border: 1px solid #3f3f46;
            border-radius: 8px;
            box-shadow: none;

            &:hover {
                border-color: #52525b;
            }

            &.is-focus {
                border-color: #99f768;
                box-shadow: none;
            }
        }

        .el-input__inner {
            color: #ffffff;

            &::placeholder {
                color: #71717a;
            }
        }
    }
}
</style>
